<template>
  <ChatModal name="settings" :title="$t('settingsPopupTitle')">
    <div slot="default">
      <div :class="$style.header">
        <div :class="$style.headerFlex">
          <Avatar :user="user" :size="51" :class="$style.avatar" />
          <div :class="$style.info">
            <div :class="$style.name">{{ user.firstName }} {{ user.lastName }}</div>
          </div>
          <img src="./../../assets/img/qrcode.png" alt="QRCode" :class="$style.qrImg" @click="openQR" />
        </div>
        <div v-if="status && isShowStatus" :class="$style.status" @click="$modal.show('set-status')">
          <BaseIcon name="info-circle" /> {{ status.statusText }}
        </div>
      </div>
      <div :class="$style.section">
        <div :class="$style.sectionItem">
          <div :class="$style.sectionItemLabel">{{ $t('formPhone') }}</div>
          <div :class="$style.sectionItemText">{{ user.phone }}</div>
        </div>
        <div v-if="user.email" :class="$style.sectionItem">
          <div :class="$style.sectionItemLabel">{{ $t('formEmail') }}</div>
          <div :class="$style.sectionItemText">{{ user.email }}</div>
        </div>
        <div v-if="user.nickname" :class="$style.sectionItem">
          <div :class="$style.sectionItemLabel">{{ $t('formNickName') }}</div>
          <div :class="$style.sectionItemText">@{{ user.nickname }}</div>
        </div>
        <div v-if="isPremium" :class="$style.sectionItem">
          <div :class="$style.sectionItemLabel">{{ $t('translation.languageLabel') }}</div>
          <Multiselect
            v-model="selectedLanguage"
            :placeholder="$t('translation.languagePlaceholder')"
            label="label"
            trackBy="value"
            :options="languages"
            :showLabels="false"
          />
        </div>
        <div :class="[$style.sectionItem, $style.switchers]">
          <BaseSwitch :value="settings.publicSearchEmail" @input="val => changeSetting(val, 'publicSearchEmail')">{{
            $t('settings.publicSearchEmail')
          }}</BaseSwitch>
          <BaseSwitch
            :value="settings.publicSearchFirstName"
            @input="val => changeSetting(val, 'publicSearchFirstName')"
            >{{ $t('settings.publicSearchFirstName') }}</BaseSwitch
          >
          <BaseSwitch
            :value="settings.publicSearchLastName"
            @input="val => changeSetting(val, 'publicSearchLastName')"
            >{{ $t('settings.publicSearchLastName') }}</BaseSwitch
          >
          <BaseSwitch
            :value="settings.publicSearchNickname"
            @input="val => changeSetting(val, 'publicSearchNickname')"
            >{{ $t('settings.publicSearchNickname') }}</BaseSwitch
          >
          <BaseSwitch :value="settings.publicSearchPhone" @input="val => changeSetting(val, 'publicSearchPhone')">{{
            $t('settings.publicSearchPhone')
          }}</BaseSwitch>
          <BaseSwitch :value="settings.notify" @input="val => changeSetting(val, 'notify')">{{
            $t('settings.notify')
          }}</BaseSwitch>
        </div>
        <div :class="$style.sectionItem">
          <div :class="$style.sectionItemLabel">{{ $t('settings.chatNotify') }}</div>
          <BaseSelect
            :value="chatNotify"
            :options="chatNotificationOptions"
            @input="option => changeSetting(option.value, 'chatNotify')"
          />
        </div>
        <div :class="$style.sectionItem">
          <div :class="$style.sectionItemLabel">{{ $t('settings.groupNotify') }}</div>
          <BaseSelect
            :value="groupNotify"
            :options="chatNotificationOptions"
            @input="option => changeSetting(option.value, 'groupNotify')"
          />
        </div>
        <div :class="$style.sectionItem" v-if="matrixSetting">
          <div style="margin-bottom: 10px">
            <span v-if="!matrixSetting.isBridgeWhatsapp" class="link" @click="$modal.show('bridge-whatsapp')">{{
              $t('connectWA')
            }}</span>
            <span v-else class="link red" @click="handleDisconnectDialog($options.messengerProviders.whatsapp)">{{
              $t('rejectWA')
            }}</span>
          </div>
          <div>
            <span v-if="!matrixSetting.isBridgeTelegram" class="link" @click="$modal.show('bridge-telegram')">{{
              $t('connectTelegram')
            }}</span>
            <span v-else class="link red" @click="handleDisconnectDialog($options.messengerProviders.telegram)">{{
              $t('rejectTG')
            }}</span>
          </div>
        </div>
      </div>
    </div>
    <BridgeTelegram />
    <BridgeWhatsApp />
    <ChatModal
      name="disconnect-bridge"
      style="min-height: auto"
      :title="$t(provider === $options.messengerProviders.whatsapp ? 'rejectWA' : 'rejectTG')"
      @before-open="beforeOpenDisconnectDialog"
    >
      <template>
        <div style="padding: 20px">
          <p style="font-size: 16px; line-height: 1.4">
            Do you really want to disable your
            {{ provider === $options.messengerProviders.whatsapp ? $t('providerWA') : $t('providerTG') }} link? Future
            {{ provider === $options.messengerProviders.whatsapp ? $t('providerWA') : $t('providerTG') }} chats will be
            hidden.
          </p>
        </div>
        <template slot="footer">
          <BaseButton
            oval
            red
            :loading="loading"
            @click.prevent="handleDisconnectMessenger()"
            style="margin-right: 15px"
            >{{ $t('buttonDisconnect') }}</BaseButton
          >
          <BaseButton oval @click.prevent="$modal.hide('disconnect-bridge')">{{ $t('buttonClose') }}</BaseButton>
        </template>
      </template>
    </ChatModal>
  </ChatModal>
</template>

<script>
import ChatModal from './ChatModal'
import Avatar from '@/components/Avatar'
import TranslatedMessage, { LANGUAGES } from '@/models/TranslatedMessage'
import Multiselect from 'vue-multiselect'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import BridgeTelegram from '@/components/modals/bridge/BridgeTelegram'
import BridgeWhatsApp from '@/components/modals/bridge/BridgeWhatsApp'
import { msgTypes, eventTypes, chatNamesForBridge, messengerProviders, selfMembershipTypes } from '@/helpers/variables'
import { parseRouterQueryParam } from '@/helpers/index'

export default {
  name: 'settings-popup',
  messengerProviders,
  components: {
    ChatModal,
    Avatar,
    Multiselect,
    BridgeTelegram,
    BridgeWhatsApp
  },
  data () {
    return {
      selectedLanguage: null,
      loading: false,
      provider: ''
    }
  },
  created () {
    if (this.translateLanguage) {
      this.selectedLanguage = this.languages.find(obj => obj.value === this.translateLanguage)
    }
  },
  computed: {
    translateLanguage () {
      return this.$store.state.entities.translatedMessages.language
    },
    ...mapGetters({
      user: 'auth/user',
      settings: 'auth/settings',
      isPremium: 'auth/isPremium',
      status: 'entities/userStatuses/myCurrentStatus',
      matrixSetting: 'matrix/matrixCredentials',
      rooms: 'matrix/rooms',
      getRoomById: 'matrix/getRoomById',
      inviteRooms: 'matrix/inviteRooms'
    }),
    languages () {
      return Object.keys(LANGUAGES).map(key => ({
        label: key,
        value: LANGUAGES[key]
      }))
    },
    chatNotificationOptions () {
      return [
        {
          label: this.$t('settings.notifOption.showWithSound'),
          value: 1
        },
        {
          label: this.$t('settings.notifOption.doNotShow'),
          value: 0
        }
      ]
    },
    chatNotify () {
      return this.chatNotificationOptions.find(option => option.value === this.settings.chatNotify)
    },
    groupNotify () {
      return this.chatNotificationOptions.find(option => option.value === this.settings.groupNotify)
    },
    isShowStatus () {
      return this.user.statusExpires && +new Date(this.user.statusExpires + 'Z') > Date.now()
    }
  },
  watch: {
    'selectedLanguage.value' (language) {
      TranslatedMessage.commit(state => {
        state.language = language
      })
    }
  },
  methods: {
    ...mapActions({
      updateSettings: 'auth/updateSettings',
      updateMatrixCredentials: 'matrix/updateMatrixCredentials',
      getMatrixCredentials: 'matrix/getMatrixCredentials'
    }),
    ...mapMutations({
      removeRoomLocally: 'matrix/REMOVE_ROOM_FROM_LIST',
      setCurrentRoom: 'matrix/SET_CURRENT_ROOM',
      setRoomMembers: 'matrix/SET_ROOM_MEMBERS',
      resetRoomMessages: 'matrix/DELETE_MESSAGES_FROM_CURRENT_ROOM'
    }),
    beforeOpenDisconnectDialog () {},
    handleDisconnectDialog (provider) {
      this.provider = provider
      this.$modal.show('disconnect-bridge')
    },
    changeSetting (value, key) {
      this.updateSettings({
        [key]: value
      })
    },
    async handleDisconnectMessenger () {
      this.loading = true
      try {
        await this.sendLogoutMessageRoom()
        const payload = {}

        if (this.provider === messengerProviders.whatsapp) {
          payload.is_bridge_whatsapp = false
          payload.whatsapp_phone = ''
        }

        if (this.provider === messengerProviders.telegram) {
          payload.is_bridge_telegram = false
          payload.telegram_phone = ''
        }

        await this.updateMatrixCredentials(payload)
        await this.getMatrixCredentials()

        if (this.$route.name === 'app-chat-matrix') {
          if (this.rooms.length) {
            const id = parseRouterQueryParam(this.$route.query.id, true)
            const room = this.getRoomById(id)
            if (!room) {
              const { name, params, query } = this.$route
              this.$router.push({ name, params, query: { ...query, id: this.rooms[0].id } })
              this.setCurrentRoom(this.rooms[0])
            }
          } else {
            this.$router.push({ name: 'app-chat' })
          }
        }
      } finally {
        this.loading = false
        this.$modal.hide('disconnect-bridge')
      }
    },
    async sendLogoutMessageRoom () {
      const rooms = window.matrixSDK.getRooms()
      const provider =
        this.provider === messengerProviders.whatsapp ? chatNamesForBridge.whatsapp : chatNamesForBridge.telegram
      const providerConnectingRoom = rooms.find(
        e => e.name === provider && e.selfMembership === selfMembershipTypes.join
      )
      const content = {
        body: 'logout',
        msgtype: msgTypes.text
      }
      if (providerConnectingRoom) {
        await window.matrixSDK.sendEvent(providerConnectingRoom.roomId, eventTypes.message, content, '')
      }
    },
    openQR () {
      this.$modal.show('share-contact-by-code', {
        nickname: this.user.nickname,
        userId: this.user.id
      })
    }
  }
}
</script>

<style lang="scss" module>
.header {
  background-color: $main-bg-colored;
  height: 90px;
  padding: 0 20px;
}
.headerFlex {
  display: flex;
  align-items: center;
}
.avatar {
  margin-right: 12px;
}
.info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: $text-lightest;
}
.name {
  font-size: 16px;
  font-weight: 500;
}
.meta {
  font-weight: 300;
}
.status {
  margin-top: 12px;
  color: $text-lightest;
  font-weight: 300;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
}

.section {
  padding: 20px; // 20px 10px 20px 82px;
}
.sectionItem {
  margin-bottom: 18px;
  &Label {
    color: $text-mid;
    margin-bottom: 4px;
  }
  &Text {
    font-size: 16px;
  }
}
.qrImg {
  height: 50px;
  width: 50px;
  cursor: pointer;
}
.switchers {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
